import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, Eye } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";

const visitorId = () => {
  let id = localStorage.getItem("documind_visitor");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("documind_visitor", id); }
  return id;
};

const viewsLabel = (n) => `${n.toLocaleString()} ${n === 1 ? "view" : "views"}`;

export default function PublicVideo() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/public/videos/${token}`).then((r) => {
      setInfo(r.data);
      document.title = `${r.data.title} — explained in 5 seconds | DocuMind`;
      api.post(`/public/videos/${token}/view`, { visitor: visitorId() })
        .then((v) => setInfo((i) => ({ ...i, view_count: v.data.view_count })))
        .catch(() => {});
    }).catch(() => setMissing(true));
  }, [token]);

  return (
    <div className="min-h-screen paper-noise flex flex-col">
      <header className="max-w-5xl w-full mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="public-video-logo">
          <div className="w-7 h-7 bg-foreground text-background grid place-items-center font-serif-display text-lg font-black">D</div>
          <span className="font-serif-display text-xl tracking-tight">DocuMind</span>
        </Link>
        <Link to="/signup" className="font-mono-label hover:text-primary">Try it free →</Link>
      </header>
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-10 fade-up">
        {missing ? (
          <div data-testid="public-video-missing" className="border border-border bg-background p-10 max-w-lg">
            <h1 className="font-serif-display text-4xl tracking-tight mb-3">This video isn't shared anymore.</h1>
            <p className="text-muted-foreground">The owner turned off the link, or it never existed.</p>
          </div>
        ) : !info ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : (
          <>
            <div className="font-mono-label text-muted-foreground mb-3">Explainer video · made with DocuMind</div>
            <h1 data-testid="public-video-title" className="font-serif-display text-4xl sm:text-5xl tracking-tighter mb-5 capitalize">{info.title}</h1>
            {info.creator && (
              <div data-testid="public-video-creator" className="flex items-center gap-3 mb-8">
                <UserAvatar name={info.creator.name} picture={info.creator.picture} size={40} testId="public-video-creator-avatar" />
                <div>
                  <div className="font-mono-label text-muted-foreground">Shared by</div>
                  <div data-testid="public-video-creator-name" className="font-medium">{info.creator.name || "A DocuMind user"}</div>
                  {info.creator.bio && <div data-testid="public-video-creator-bio" className="text-sm text-muted-foreground">{info.creator.bio}</div>}
                </div>
              </div>
            )}
            <div data-testid="public-video-views" className="font-mono-label text-muted-foreground mb-3 flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> {viewsLabel(info.view_count || 0)}
            </div>
            <video data-testid="public-video-player" src={`${API}/public/videos/${token}/file`} controls playsInline
              poster={info.has_poster ? `${API}/public/videos/${token}/poster.jpg` : undefined}
              className="w-full border border-foreground/15 bg-black shadow-[8px_8px_0_0_hsl(var(--primary))]" />
            <div className="mt-12 flex flex-wrap items-center justify-between gap-6 border-t border-border pt-8">
              <p className="text-muted-foreground max-w-md">Turn any PDF into answers, summaries and videos like this one. Every answer cites the page it came from.</p>
              <Link to="/signup" data-testid="public-video-cta">
                <Button className="rounded-sm bg-foreground text-background hover:bg-primary btn-invert h-12 px-6">Make your own <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
